import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, Loader2, Copy, Check, Download } from "lucide-react";

export default function SocialImageShareModal({ image, onClose }) {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api.adminSocialImageShare(image.id);
        if (!cancelled) setShare(data);
      } catch {
        if (!cancelled) toast.error("No se pudo crear el link corto");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [image.id]);

  const copyLink = async () => {
    if (!share?.short_url) return;
    try {
      await navigator.clipboard.writeText(share.short_url);
      setCopied(true);
      toast.success("Link copiado al portapapeles");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const download = async () => {
    try {
      const resp = await api.adminSocialImageDownload(image.id);
      const cd = resp?.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/i.exec(cd);
      const filename = m ? m[1] : `${image.format || "post"}-placa.jpg`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Descarga iniciada");
    } catch {
      toast.error("No se pudo descargar la imagen");
    }
  };

  return (
    <div
      data-testid="share-modal"
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-3xl w-full max-h-[95vh] overflow-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-softblue px-5 py-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-bold">Compartir placa</div>
            <div className="text-sm text-nightblue">{image.width}×{image.height} · {image.format}</div>
          </div>
          <button
            data-testid="share-close"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center hover:bg-lightgray"
          >
            <X className="w-5 h-5 text-blueblack" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-5 p-5">
          <img
            src={image.file_url}
            alt="placa"
            className="w-full h-auto object-contain bg-blueblack shadow-lg"
          />

          <div className="space-y-4 min-w-0">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-nightblue mb-1">Link corto</div>
              {loading ? (
                <div className="flex items-center gap-2 text-textgray text-sm h-10">
                  <Loader2 className="w-4 h-4 animate-spin" /> Generando link…
                </div>
              ) : share ? (
                <div className="flex items-center gap-2">
                  <input
                    data-testid="share-short-url"
                    readOnly
                    value={share.short_url}
                    className="flex-1 h-10 px-2 border border-softblue text-sm font-mono bg-lightgray"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    data-testid="share-copy"
                    onClick={copyLink}
                    className="h-10 bg-nightblue hover:bg-blueblack text-white rounded-none text-[11px] uppercase tracking-widest font-bold px-3"
                  >
                    {copied ? <><Check className="w-3.5 h-3.5 mr-1" /> Copiado</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copiar</>}
                  </Button>
                </div>
              ) : null}
              <div className="text-[10px] text-textgray mt-1.5">
                Al pegar en WhatsApp o Telegram se ve la miniatura de la placa automáticamente.
              </div>
            </div>

            {share && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-nightblue mb-2">Compartir directo</div>
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={share.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-whatsapp"
                    className="h-10 px-4 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ea955] text-white text-[11px] uppercase tracking-widest font-bold"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/></svg>
                    WhatsApp
                  </a>
                  <a
                    href={share.telegram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-telegram"
                    className="h-10 px-4 inline-flex items-center gap-2 bg-[#229ED9] hover:bg-[#1a86bb] text-white text-[11px] uppercase tracking-widest font-bold"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                    Telegram
                  </a>
                  <Button
                    data-testid="share-download"
                    onClick={download}
                    variant="outline"
                    className="h-10 border-softblue text-nightblue rounded-none text-[11px] uppercase tracking-widest font-bold"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Descargar
                  </Button>
                </div>
              </div>
            )}

            <div className="border-t border-softblue pt-3 text-[11px] text-textgray leading-relaxed">
              <b className="text-nightblue">Cómo funciona:</b> el link corto abre una página con la placa en HD y los meta-tags necesarios para que WhatsApp y Telegram muestren la miniatura al pegarlo. También se puede compartir en Twitter/X, Threads, o cualquier chat.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
